<template>
    <validation-observer ref="payment_request_modal" v-slot="{ meta }">
        <modal :id="modalId" title="Payment Request" scrollable ok-title="Send" @show="fetchAllData"
            :ok-disabled="!meta.valid || loading" @ok.prevent="requestPayment" @shown="setData">
            <busy-modal :show="loading"></busy-modal>
            <b-row>
                <b-col>
                    <validation-provider v-slot="{ handleChange, errors }" name="to_email" rules="email|required">
                        <form-input floatingLabel="To:" v-model="to_email" :error="errors[0]" @input="handleChange" />
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <validation-provider v-slot="{ handleChange, errors }" name="subject" rules="required">
                        <form-input floatingLabel="Subject:" v-model="subject" :error="errors[0]"
                            @input="handleChange" />
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <validation-provider name="amount" v-slot="{ errors, handleChange }"
                        rules="validPositiveFloat|minValue:0.99|required">
                        <form-input floating-label="Amount" v-model="amount" min="0" step="0.01" type="number"
                            :rightButton="{
                                title: '',
                                text: 'Amount to Confirm',
                                variant: 'outline-primary',
                                toggle: false,
                            }" @handle-right-button-click="setAmountToConfirm()" :rightButton2="{
                                title: '',
                                text: 'Amount to Balance',
                                variant: 'outline-primary',
                                toggle: false,
                            }" @handle-right-button-2-click="setAmountToBalance()" :degrade-on-small="true"
                            drop-down-text="Select Amount" :error="errors[0]" @input="handleChange" />
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row class="no-gutters">
                <b-col md="9">
                    <form-select :options="emailResponseOptions" v-model="selectedEmailResponseOption"
                        floatingLabel="Email Response" placeholder="Insert email template response"
                        :disabled="emailResponseOptionsDisabled" />
                </b-col>
                <b-col md="3" class="pb-3 pl-md-2 pb-md-0">
                    <b-button class="right-button" variant="primary" @click="userCanUseUnlayer ? applyUnlayerTemplateResponse() : applyResponse()"
                        :disabled="isNil(selectedEmailResponseOption) || emailResponseOptionsDisabled">Apply</b-button>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <validation-provider v-slot="{ handleChange, errors }" name="message" rules="required">
                        <lx-editor v-if="!reservation.airbnb_messaging_api && !reservation.vrbo_messaging_api" v-model="message" :error="errors[0]"
                            @input="handleChange"></lx-editor>

                        <form-textarea v-if="reservation.airbnb_messaging_api" v-model="message" :error="errors[0]"
                            rows="6" @input="handleChange"></form-textarea>
                    </validation-provider>
                </b-col>
            </b-row>
        </modal>
    </validation-observer>
</template>

<script>
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import API from '../../../../../api.jsx';
import Modal from '../../../../generic/modal/modal.vue';
import BusyModal from '../../../../generic/busy-modal/busy-modal.vue';
import LxEditor from '../../../../generic/lx-editor/lx-editor.vue';
import FormInput from '../../../../generic/form-input/form-input.vue';
import FormSelect from '../../../../generic/form-select/form-select.vue';
import FormTextarea from '../../../../generic/form-textarea/form-textarea.vue';
import { formatResponseError } from '../../../../../utils/api';
import {
    APP_EMAIL_MOCKUP_RESPONSES,
    APP_EMAIL_RESPONSE_PAYMENT_REQUEST,
    GUEST_RESERVATION_REQUEST_PAYMENT_ENDPOINT,
    UNLAYER_TEMPLATE_BY_ID_ENDPOINT,
} from '../../../../../endpoints';

export default {
    name: 'PaymentRequestModal',
    components: {
        Modal,
        BusyModal,
        LxEditor,
        FormInput,
        FormSelect,
        FormTextarea,
    },
    props: {
        modalId: String,
        company: String,
        reservation: Object,
        onUpdate: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            loading: false,
            subject: null,
            to_email: null,
            selectedEmailResponseOption: null,
            emailResponseOptions: [],
            message: '',
            amount: 0,
        };
    },
    inject: ['showSuccessToast', 'showErrorToast'],
    created() {
        this.subject = `Payment request from ${this.company}`;
    },
    computed: {
        ...mapGetters('header', ['userCanUseUnlayer']),
        emailResponseOptionsDisabled() {
            return this.emailResponseOptions.length < 2;
        },
    },
    methods: {
        isNil,
        setAmountToConfirm() {
            this.amount = this.reservation.remainders.remain_to_deposit;
            this.$refs.payment_request_modal.setFieldValue('amount', this.amount);
        },
        setAmountToBalance() {
            this.amount = this.reservation.remainders.remain_to_whole;
            this.$refs.payment_request_modal.setFieldValue('amount', this.amount);
        },
        async applyResponse() {
            try {
                const { data } = await API.get(
                    APP_EMAIL_MOCKUP_RESPONSES(this.selectedEmailResponseOption, this.reservation.id || 0)
                );
                this.message = data;
            } catch (err) {
                this.showErrorToast({ text: 'Could not fetch response message.' });
            }
        },
        setData() {
            this.$refs.payment_request_modal.setValues({
                to_email: this.to_email,
                subject: this.subject,
                amount: this.amount,
                message: this.message,
            });
        },
        fetchAllData() {
            if (this.userCanUseUnlayer) {
                this.fetchUnlayerTemplates();
            } else {
                this.fetchData();
            }
        },
        async fetchData() {
            try {
                const { data } = await API.get(APP_EMAIL_RESPONSE_PAYMENT_REQUEST());
                this.emailResponseOptions = data.map((item) => ({
                    value: item.id,
                    text: item.title,
                }));
                this.selectedEmailResponseOption = data.find((item) => item.is_default_payment_request_email)?.id;
                if (!this.selectedEmailResponseOption) {
                    this.selectedEmailResponseOption = data.length ? data[0].id : '';
                }
                this.to_email = this.reservation.user.email;
                await this.applyResponse();
                this.setData();
            } catch (err) {
                this.showErrorToast({
                    text: 'Could not fetch response options.',
                });
            }
        },
        async fetchUnlayerTemplates() {
            try {
                const { data } = await API.get(UNLAYER_TEMPLATE_BY_ID_ENDPOINT() + 'payment-requests-responses/');
                this.emailResponseOptions = data.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
                this.selectedEmailResponseOption = data.find((item) => item.identifier === 'payment_request')?.id;
                if (!this.selectedEmailResponseOption) {
                    this.selectedEmailResponseOption = data.length ? data[0].id : '';
                }
                this.to_email = this.reservation.user.email;
                await this.applyUnlayerTemplateResponse();
                this.setData();
            } catch (err) {
                this.showErrorToast({ text: 'Could not fetch response options.' });
            }
        },
        async applyUnlayerTemplateResponse() {
            try {
                const { data } = await API.post(
                    UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.selectedEmailResponseOption) + 'apply-template/',
                    {
                        meta_reservation_id: this.reservation.id,
                    }
                );
                this.message = data;
            } catch (err) {
                this.showErrorToast({ text: 'Could not apply unlayer template response.' });
            }
        },
        async requestPayment() {
            try {
                this.loading = true;
                await API.post(
                    GUEST_RESERVATION_REQUEST_PAYMENT_ENDPOINT(this.reservation.id, this.reservation.user.id),
                    {
                        payment_amount: this.amount,
                        to: this.to_email,
                        subject: this.subject,
                        email_body: this.message,
                    }
                );
                this.$bvModal.hide(this.modalId);
                this.onUpdate();
                this.showSuccessToast({ text: 'Payment request sent.' });
            } catch (e) {
                const { generic, fields } = formatResponseError(e);
                if (!isEmpty(fields)) this.$refs.payment_request_modal.setErrors(fields);
                this.showErrorToast({
                    text: generic || 'Could not request payment.',
                });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.right-button {
    padding-top: 0.45rem !important;
    padding-bottom: 0.45rem !important;
    width: 100%;
    height: 2.75rem;
}
</style>
